import { FILE_TYPES, fileExtension, isCompressibleImage, TFileAccept } from './fileTypes'

export interface IFileRejection {
  title: string
  description: string
}

export interface IFileLimits {
  /** Token do registro em `fileTypes.ts`. */
  accept: TFileAccept
  /** Tamanho máximo por arquivo, em MB. Espelhe o `max:` da regra do endpoint. */
  maxSizeMB?: number
  /** Quantidade máxima de arquivos, contando os já escolhidos. */
  maxFiles?: number
  /** Soma máxima dos tamanhos, em MB — o limite do request, não o do arquivo. */
  maxTotalSizeMB?: number
}

const megabytes = (mb: number) => mb * 1024 * 1024

/**
 * Recomprime uma imagem grande em vez de recusá-la.
 *
 * Uma foto de documento tirada no celular passa fácil dos 5MB do anexo, e até aqui a única
 * resposta do painel era "arquivo muito grande" — sem dizer o que fazer com ele. O
 * `browser-image-compression` já estava no `package.json` desde a submissão de obras e ficou sem
 * consumidor quando aquela página saiu; é este o uso que faltava.
 *
 * Só entra em cena quando o arquivo **já estouraria o limite**, então o caminho normal segue sem
 * custo nenhum. A biblioteca é carregada sob demanda para não pesar o bundle inicial, e qualquer
 * falha dela (formato exótico, canvas bloqueado) devolve `null` — aí vale a recusa de sempre.
 */
async function compressToFit(file: File, maxSizeMB: number): Promise<File | null> {
  if (!isCompressibleImage(file)) return null

  try {
    const { default: imageCompression } = await import('browser-image-compression')
    const compressed = await imageCompression(file, {
      maxSizeMB,
      maxWidthOrHeight: 2560,
      useWebWorker: true
    })

    if (compressed.size >= file.size) return null

    // Reembrulha para garantir nome e extensão originais — o backend valida por `mimes`, que o
    // Laravel resolve pela extensão do arquivo enviado. O `lastModified` do arquivo escolhido vem
    // junto porque é ele que identifica o arquivo na checagem de duplicata: carimbar `Date.now()`
    // aqui apagaria a única marca que sobrevive à recompressão.
    return new File([compressed], file.name, { type: compressed.type || file.type, lastModified: file.lastModified })
  } catch {
    return null
  }
}

/**
 * Valida os arquivos escolhidos contra o token e os limites do campo, descartando duplicatas do
 * que já foi selecionado. Devolve os aceitos (já recomprimidos, quando foi o caso) e os recusados
 * com o motivo, para o chamador exibir como preferir.
 *
 * Havia duas cópias disto, e elas **discordavam no que importa**: a da entrega de trabalho valida
 * por extensão, a do aproveitamento de estudos validava por `file.type`. A segunda recusa um PDF
 * que o navegador entrega com `type` vazio — o que acontece de verdade com arquivo arrastado do
 * gerenciador e em parte dos navegadores de celular. Ficou a validação por extensão.
 */
export async function validatePickedFiles(
  selected: File[],
  { accept, maxSizeMB, maxFiles, maxTotalSizeMB }: IFileLimits,
  existing: File[] = []
): Promise<{ accepted: File[]; rejected: IFileRejection[] }> {
  const { extensions, label } = FILE_TYPES[accept]
  const accepted: File[] = []
  const rejected: IFileRejection[] = []

  let totalSize = existing.reduce((sum, file) => sum + file.size, 0)

  for (const picked of selected) {
    if (!extensions.includes(fileExtension(picked.name))) {
      rejected.push({
        title: `"${picked.name}" não é um tipo permitido`,
        description: `Envie um arquivo ${label}`
      })
      continue
    }

    // Nome + `lastModified`, e não nome + tamanho: o tamanho é justamente o que a recompressão
    // muda. Uma foto de 8MB guardada com 4MB não batia mais com os 8MB do mesmo arquivo escolhido
    // de novo, então ela entrava duas vezes e subia duplicada. O `lastModified` é o mtime do
    // arquivo em disco e atravessa a recompressão intacto.
    const isDuplicate = (file: File) => file.name === picked.name && file.lastModified === picked.lastModified
    if (existing.some(isDuplicate) || accepted.some(isDuplicate)) {
      rejected.push({
        title: `"${picked.name}" já foi adicionado`,
        description: 'Este arquivo já está na lista'
      })
      continue
    }

    if (maxFiles && existing.length + accepted.length >= maxFiles) {
      rejected.push({
        title: `"${picked.name}" não foi adicionado`,
        description: `É possível enviar no máximo ${maxFiles} ${maxFiles === 1 ? 'arquivo' : 'arquivos'}`
      })
      continue
    }

    let file = picked
    if (maxSizeMB && file.size > megabytes(maxSizeMB)) {
      file = (await compressToFit(file, maxSizeMB)) ?? file
    }

    if (maxSizeMB && file.size > megabytes(maxSizeMB)) {
      rejected.push({
        title: `"${picked.name}" é muito grande`,
        description: `Cada arquivo deve ter no máximo ${maxSizeMB}MB`
      })
      continue
    }

    if (maxTotalSizeMB && totalSize + file.size > megabytes(maxTotalSizeMB)) {
      rejected.push({
        title: `"${picked.name}" não foi adicionado`,
        description: `O total dos arquivos passaria de ${maxTotalSizeMB}MB`
      })
      continue
    }

    totalSize += file.size
    accepted.push(file)
  }

  return { accepted, rejected }
}
